import React, { useMemo, useState } from "react";
import { Laptop, Smartphone, IdCard, Package, X, Headphones, Wifi, AlertTriangle } from "lucide-react";
import useMyAssets from "../hooks/UseMyAssets";

const ICONS_BY_CATEGORY = {
  Laptop: Laptop,
  Mobile: Smartphone,
  Phone: Smartphone,
  "ID Card": IdCard,
  Headset: Headphones,
  Broadband: Wifi,
};

const getIcon = (category) => ICONS_BY_CATEGORY[category] || Package;

const STATUS_STYLES = {
  ACTIVE: { bg: "#CFFFDE", text: "#003C18" },
  RETURNED: { bg: "#F5F5F5", text: "#7F7F7F" },
  LOST_DAMAGED: { bg: "#FFE1E1", text: "#7A0000" },
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.RETURNED;
  return (
    <span
      className="px-3 py-1 rounded-full text-xs font-normal"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status?.replace("_", "/") || "UNKNOWN"}
    </span>
  );
}

// "filterKey" is null for the Total card (no filtering), otherwise it's the
// asset.status value that clicking this card should filter the list down to.
function StatCard({ label, value, sub, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative bg-white rounded-lg flex-1 min-w-[220px] text-left transition"
      style={{
        boxShadow: active
          ? "0px 6px 16px rgba(0,0,0,0.08)"
          : "0px 6px 16px rgba(0,0,0,0.04)",
        outline: active ? `2px solid ${color}` : "none",
        outlineOffset: "-1px",
      }}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
        style={{ backgroundColor: color }}
      />
      <div className="pl-5 pr-4 py-3">
        <p className="text-xs font-medium text-black">{label}</p>
        <p
          className="text-2xl font-semibold text-black mt-2"
          style={{ fontFamily: "'poppins', sans-serif" }}
        >
          {value}
        </p>
        <p className="text-[10px] font-medium text-[#7F7F7F] mt-1">{sub}</p>
      </div>
    </button>
  );
}

function AssetCard({ asset }) {
  const Icon = getIcon(asset.category);

  const fields = [
    asset.brand && { label: "Brand", value: asset.brand },
    asset.modelName && { label: "Model Name", value: asset.modelName },
    asset.serialNumber && { label: "Serial Number", value: asset.serialNumber },
    asset.category === "ID Card" && { label: "Asset", value: asset.name || "Employee ID" },
    { label: "Assign Date", value: formatDate(asset.assignDate) },
    { label: "Return Date", value: formatDate(asset.returnDate) },
  ].filter(Boolean);

  return (
    <div
      className="bg-white rounded-2xl p-6"
      style={{ boxShadow: "0px 0px 4px rgba(0,0,0,0.05)" }}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-[54px] h-[51px] rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "#D2E0FB" }}
          >
            <Icon size={24} color="#4379FE" />
          </div>
          <span className="text-base text-black capitalize">
            {asset.category}
          </span>
        </div>
        <StatusBadge status={asset.status} />
      </div>

      <div className="border-t border-black/5 mb-2" />

      <div className="space-y-4 mt-4">
        {fields.map((f) => (
          <div key={f.label} className="flex items-center justify-between">
            <span className="text-base text-black/50">{f.label}</span>
            <span className="text-base text-black">{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Raise Query opens a Gmail compose window for one asset ──
const QUERY_RECIPIENT = process.env.REACT_APP_QUERY_RECIPIENT_EMAIL;
const describeAsset = (a = {}) =>
  `${a.category || "Asset"}${a.modelName ? ` — ${a.modelName}` : ""}${
    a.serialNumber ? ` (${a.serialNumber})` : ""
  }`;

// Returns an error message on failure, or null on success — lets callers
// surface a real in-app message instead of failing silently.
const openGmailCompose = (asset) => {
  if (!QUERY_RECIPIENT) {
    console.error(
      "REACT_APP_QUERY_RECIPIENT_EMAIL is not set. Add it to your .env file and restart the dev server.",
    );
    return "Raise Query is not configured yet. Please contact your admin.";
  }
  const subject = `Query regarding ${describeAsset(asset)}`;
  const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
    QUERY_RECIPIENT,
  )}&su=${encodeURIComponent(subject)}`;
  // window.open() with "noopener" always returns null on success (that's
  // the point of noopener — no reference back to the new window), so a
  // null return here can't be used to detect a blocked popup. Only an
  // exception (window.open unsupported/disabled) counts as a real failure.
  try {
    window.open(url, "_blank", "noopener,noreferrer");
    return null;
  } catch {
    return "Your browser blocked the popup. Please allow popups for this site and try again.";
  }
};

// Shown only when the user has more than one asset — pick which asset the
// query is about, then redirect to Gmail compose.
function AssetPickerModal({ open, assets, onClose, onError }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-black">Raise a Query</h2>
          <button onClick={onClose} className="text-black/40 hover:text-black">
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-black/50 mb-3">
          Select the asset your query is about
        </p>

        <div className="max-h-[300px] overflow-y-auto divide-y divide-black/5">
          {assets.map((a) => {
            const Icon = getIcon(a.category);
            return (
              <button
                key={a._id}
                onClick={() => {
                  const err = openGmailCompose(a);
                  onClose();
                  if (err) onError(err);
                }}
                className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-black/5 rounded-lg"
              >
                <span className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-black/60" />
                </span>
                <span className="text-sm text-black">{describeAsset(a)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MyAssets() {
  const { assets, summary, isLoading, error } = useMyAssets();
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [queryError, setQueryError] = useState("");

  // "all" | "ACTIVE" | "LOST_DAMAGED" | "RETURNED"
  const [activeFilter, setActiveFilter] = useState("all");

  const handleRaiseQuery = () => {
    if (assets.length === 1) {
      // Only one asset — no need to ask, go straight to Gmail
      const err = openGmailCompose(assets[0]);
      if (err) setQueryError(err);
    } else {
      setShowAssetPicker(true);
    }
  };

  const handleStatClick = (filterKey) => {
    // Clicking the already-active card resets the filter (shows all again)
    setActiveFilter((prev) => (prev === filterKey ? "all" : filterKey));
  };

  const filteredAssets = useMemo(() => {
    if (activeFilter === "all") return assets;
    return assets.filter((a) => a.status === activeFilter);
  }, [assets, activeFilter]);

  const stats = [
    { label: "Total Assets",  value: summary.totalAssets,  sub: "All assets",        color: "#ffff", filterKey: "all" },
    { label: "Active Assets", value: summary.activeAssets, sub: "In use",             color: "#513CF6", filterKey: "ACTIVE" },
    { label: "Lost/Damaged",  value: summary.lostDamaged,  sub: "Critical assets",    color: "#F50B0B", filterKey: "LOST_DAMAGED" },
    { label: "Returned",      value: summary.returned,     sub: "Completed returns",  color: "#22C55E", filterKey: "RETURNED" },
  ];

  return (
    <div className="min-h-full bg-[#F1F5F9] px-4 md:px-6 py-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="w-full">

        {/* Header */}
        <div
          className="bg-white rounded-lg flex items-center justify-between px-6 py-6 mb-6"
          style={{ boxShadow: "0px 6px 16px rgba(0,0,0,0.04)" }}
        >
          <div>
            <h1 className="text-2xl font-medium text-black capitalize">My Assets</h1>
            <p className="text-base text-black/50 mt-1">
              View all company assets assigned to your profile
            </p>
          </div>
          <button
            onClick={handleRaiseQuery}
            disabled={isLoading || assets.length === 0}
            className="text-white text-sm rounded-lg px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#4253C7" }}
          >
            Raise Query
          </button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4 mb-4">
          {stats.map((s) => (
            <StatCard
              key={s.label}
              label={s.label}
              value={s.value}
              sub={s.sub}
              color={s.color}
              active={activeFilter === s.filterKey}
              onClick={() => handleStatClick(s.filterKey)}
            />
          ))}
        </div>


        {/* Error state */}
        {error && (
          <div className="bg-red-50 text-red-700 text-sm rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 h-[260px] animate-pulse"
                style={{ boxShadow: "0px 0px 4px rgba(0,0,0,0.05)" }}
              />
            ))}
          </div>
        )}

        {/* Empty state (no assets at all) */}
        {!isLoading && !error && assets.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center text-black/50">
            No assets have been assigned to you yet.
          </div>
        )}

        {/* Empty state (filter matched nothing) */}
        {!isLoading && !error && assets.length > 0 && filteredAssets.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center text-black/50">
            No assets match this filter.
          </div>
        )}

        {/* Asset cards */}
        {!isLoading && filteredAssets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filteredAssets.map((asset) => (
              <AssetCard key={asset._id} asset={asset} />
            ))}
          </div>
        )}
      </div>

      <AssetPickerModal
        open={showAssetPicker}
        assets={assets}
        onClose={() => setShowAssetPicker(false)}
        onError={setQueryError}
      />

      {/* Raise Query error — surfaced in-app instead of failing silently */}
      {queryError && (
        <div className="fixed inset-0 bg-black/30 z-[2000] flex items-center justify-center p-4">
          <div className="w-full max-w-[420px] bg-white rounded-xl shadow-2xl p-6 relative text-center">
            <button
              onClick={() => setQueryError("")}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-500" size={22} />
            </div>
            <h2 className="text-base font-bold text-black mb-2">
              Unable to Raise Query
            </h2>
            <p className="text-sm text-gray-500 mb-6">{queryError}</p>
            <button
              onClick={() => setQueryError("")}
              className="w-full h-10 bg-[#4253C7] text-white rounded-lg text-sm font-semibold hover:bg-[#3644b0]"
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </div>
  );
}